import React, { useState, useEffect, useRef } from "react";
import { ListPageSkeleton } from "../../Components/Skeleton";
import SearchBar from "../../Components/SearchBar";
import Button from "../../Components/Button";
import { IoMdAdd, IoMdClose, IoMdSearch } from "react-icons/io";
import { Modal } from "../../Components/Modal";
import { supabase } from "../../supabaseClient";
import {
  MdModeEditOutline,
  MdDelete,
  MdContentCopy,
  MdCheckCircle,
} from "react-icons/md";
import { FiUser, FiDatabase, FiVideo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";

function Directors() {
  const [showModal, setShowModal] = useState(false);
  const [directorName, setDirectorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [directors, setDirectors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editDirector, setEditDirector] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const debouncedSearch = useDebounce(searchTerm, 400);

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef(null);

  // Debounced search hook
  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const fetchDirectors = async (pageToFetch = 0, isNew = false) => {
    if (loadingMore) return;
    setLoadingMore(true);

    let query = supabase
      .from("directors")
      .select("*")
      .range(pageToFetch * PAGE_SIZE, (pageToFetch + 1) * PAGE_SIZE - 1);

    if (debouncedSearch.trim() !== "") {
      query = query.ilike("directors", `%${debouncedSearch}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching directors:", error);
    } else {
      if (isNew) {
        setDirectors(data);
      } else {
        setDirectors((prev) => [...prev, ...data]);
      }
      if (data.length < PAGE_SIZE) {
        setHasMore(false);
      }
    }

    setLoadingMore(false);
    setIsLoading(false);
  };

  const handleAddOrUpdateDirector = async () => {
    if (!directorName.trim()) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Please enter a director name",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
        },
      });
      return;
    }
    setLoading(true);
    try {
      if (editDirector) {
        const { error } = await supabase
          .from("directors")
          .update({ directors: directorName.trim() })
          .eq("id", editDirector.id);

        if (error) {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Failed to update director",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Director updated successfully",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        }
      } else {
        const { error } = await supabase
          .from("directors")
          .insert([{ directors: directorName.trim() }]);
        if (error) {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: "Failed to add director",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Director added successfully",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            toast: true,
            background: "white",
            customClass: {
              popup: "shadow-xl rounded-xl p-3",
            },
          });
        }
      }
      setShowModal(false);
      setDirectorName("");
      setEditDirector(null);
      await fetchDirectors(0, true);
    } catch (err) {
      console.error("Unexpected error:", err);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
        },
      });
    }
    setLoading(false);
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteDirector = async (id) => {
    try {
      const { data: dirData, error: dirError } = await supabase
        .from("directors")
        .select("directors, id")
        .eq("id", id)
        .single();

      if (dirError || !dirData) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Director not found",
          timer: 3000,
          toast: true,
          showConfirmButton: false,
          background: "white",
          customClass: { popup: "shadow-xl rounded-xl p-3" },
        });
        return;
      }

      const { count } = await supabase
        .from("tiles")
        .select("count", { count: "exact", head: true })
        .contains("director", [id]);

      const tileCount = count || 0;
      const shortId = dirData.id.slice(0, 10);

      const result = await Swal.fire({
        icon: "warning",
        title: "Delete Director",
        html: `
          <div class="text-left">
            <p class="mb-2">Are you sure you want to delete <strong class="text-red-600">"${dirData.directors}"</strong>?</p>
            <p class="text-sm text-gray-500 mb-2">UUID: ${shortId}</p>
            <p class="text-sm text-orange-600">⚠️ This director is used in ${tileCount} tile${tileCount !== 1 ? "s" : ""}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        confirmButtonText: "Yes, delete it",
        cancelButtonText: "Cancel",
        background: "white",
        customClass: {
          popup: "shadow-xl rounded-xl p-3",
          confirmButton: "rounded-lg px-4 py-2",
          cancelButton: "rounded-lg px-4 py-2",
        },
      });

      if (!result.isConfirmed) return;

      const { data: tiles } = await supabase
        .from("tiles")
        .select("id, director")
        .contains("director", [id]);

      if (tiles && tiles.length > 0) {
        for (const tile of tiles) {
          let parsed = [];
          try {
            parsed = JSON.parse(tile.director || "[]");
          } catch {
            parsed = [];
          }
          const newDirectors = parsed.filter((d) => d !== id);
          await supabase
            .from("tiles")
            .update({ director: JSON.stringify(newDirectors) })
            .eq("id", tile.id);
        }
      }

      const { error } = await supabase.from("directors").delete().eq("id", id);

      if (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Failed to delete director",
          timer: 3000,
          toast: true,
          showConfirmButton: false,
          background: "white",
          customClass: { popup: "shadow-xl rounded-xl p-3" },
        });
        return;
      }

      setDirectors((prev) => prev.filter((item) => item.id !== id));

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Director deleted successfully",
        timer: 3000,
        toast: true,
        showConfirmButton: false,
        background: "white",
        customClass: { popup: "shadow-xl rounded-xl p-3" },
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "An unexpected error occurred",
        timer: 3000,
        toast: true,
        showConfirmButton: false,
        background: "white",
        customClass: { popup: "shadow-xl rounded-xl p-3" },
      });
    }
  };

  useEffect(() => {
    if (showModal && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showModal]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setDirectors([]);
    setIsLoading(true);
    fetchDirectors(0, true);
  }, [debouncedSearch]);

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el || loadingMore || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchDirectors(nextPage);
      }
    };

    const el = containerRef.current;
    if (el) el.addEventListener("scroll", handleScroll);
    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
    };
  }, [page, hasMore, loadingMore]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="h-full flex flex-col p-6 relative">
        {/* Header Section with Animation */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <div className="flex flex-wrap justify-between items-center gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Director Management
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage and organize your film directors
              </p>
            </div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0"
            >
              <Button
                variant="primary"
                onClick={() => navigate("/directors/create")}
                className="px-5 py-2.5 text-sm flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <IoMdAdd className="text-lg" />
                Add Director
              </Button>
            </motion.div>
          </div>
        </motion.div>

        {/* Search Bar with Animation */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-6"
        >
          <div className="relative">
            <IoMdSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            <SearchBar
              placeholder="Search directors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputClassName="pl-12 py-3 border-2 border-gray-200 focus:border-cyan-400 rounded-xl transition-all duration-300"
            />
          </div>
        </motion.div>

        {/* Directors Container */}
        <motion.div
          ref={containerRef}
          className="flex-1 overflow-auto rounded-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header Stats */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiUser className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      All Directors
                    </h2>
                   
                  </div>
                </div>
                {searchTerm && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm"
                  >
                    Search: "{searchTerm}"
                  </motion.div>
                )}
              </div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="p-6">
                <ListPageSkeleton count={6} />
              </div>
            ) : directors.length === 0 ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-20"
              >
                <div className="text-6xl mb-4">🎬</div>
                <p className="text-gray-500 text-lg font-medium">
                  No directors found
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Click the "Add Director" button to create your first director
                </p>
              </motion.div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="divide-y divide-gray-100"
              >
                <AnimatePresence>
                  {directors.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={itemVariants}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      whileHover={{ scale: 1.01 }}
                      transition={{ duration: 0.2 }}
                      onHoverStart={() => setHoveredCard(item.id)}
                      onHoverEnd={() => setHoveredCard(null)}
                      className="group relative bg-white hover:bg-gradient-to-r hover:from-cyan-50/30 hover:to-transparent transition-all duration-300 cursor-pointer"
                      onClick={(e) => {
                        const isButton = e.target.closest("button");
                        if (!isButton) {
                          navigate(
                            `/tile?director=${encodeURIComponent(item.id)}`,
                          );
                        }
                      }}
                    >
                      {/* Animated border on hover */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/0 to-cyan-500/0 pointer-events-none"
                        animate={{
                          background:
                            hoveredCard === item.id
                              ? "linear-gradient(90deg, rgba(6,182,212,0) 0%, rgba(6,182,212,0.05) 50%, rgba(6,182,212,0) 100%)"
                              : "linear-gradient(90deg, rgba(6,182,212,0) 0%, rgba(6,182,212,0) 100%)",
                        }}
                        transition={{ duration: 0.3 }}
                      />

                      <div className="relative px-6 py-4">
                        <div className="flex justify-between items-center gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Director Name with Animation */}
                            <div className="flex items-center gap-3 mb-2">
                              
                                <FiVideo className="text-blue-500 text-xl" />
                           
                              <motion.h3
                                className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2"
                                animate={{
                                  color:
                                    hoveredCard === item.id
                                      ? "#2563eb"
                                      : "#1f2937",
                                }}
                              >
                                {item.directors}
                              </motion.h3>
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{
                                  scale: hoveredCard === item.id ? 1 : 0,
                                }}
                                className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full"
                              >
                                Click to view tiles
                              </motion.span>
                            </div>

                            {/* ID Section with Copy */}
                            <div className="flex items-center gap-2 mt-2">
                              <FiDatabase className="text-gray-400 text-sm" />
                              <span className="text-xs text-gray-400 font-mono">
                                ID: {item.id.slice(0, 8)}...
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(item.id, `id-${item.id}`);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                              >
                                {copiedId === `id-${item.id}` ? (
                                  <MdCheckCircle className="text-green-500" />
                                ) : (
                                  <MdContentCopy className="text-sm" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 flex-shrink-0">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => navigate(`/directors/${item.id}`)}
                              className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition-all duration-300"
                            >
                              <MdModeEditOutline className="text-lg" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDeleteDirector(item.id)}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all duration-300"
                            >
                              <MdDelete className="text-lg" />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Loading More Indicator */}
                {loadingMore && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-6"
                  >
                    <div className="inline-flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-500 border-t-transparent"></div>
                      <p className="text-gray-500 text-sm">
                        Loading more directors...
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Premium Modal */}
      <Modal
        customClasses="w-full sm:max-w-lg md:max-w-2xl"
        showModal={showModal}
        setShowModal={setShowModal}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Modal Header Gradient */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500"></div>

          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <motion.h2
                className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent"
                initial={{ x: -20 }}
                animate={{ x: 0 }}
              >
                {editDirector ? "Edit Director" : "Add New Director"}
              </motion.h2>
              <motion.button
                whileHover={{ rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoMdClose className="text-2xl" />
              </motion.button>
            </div>

            <motion.div
              className="space-y-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Director Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                  <input
                    type="text"
                    placeholder="e.g., Christopher Nolan, Steven Spielberg"
                    value={directorName}
                    onChange={(e) => setDirectorName(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition-all duration-300"
                    ref={inputRef}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Enter the director's full name as it should appear in the
                  system
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all duration-300"
                >
                  Cancel
                </motion.button>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="primary"
                    onClick={handleAddOrUpdateDirector}
                    disabled={loading}
                    className="px-6 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Saving...
                      </div>
                    ) : editDirector ? (
                      "Update Director"
                    ) : (
                      "Create Director"
                    )}
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Modal>
    </div>
  );
}

export default Directors;
